<template>
  <MmDialog :model-value="visible" :title="title" :description="description" @close="dismiss">
    <div class="upgrade-dialog">
      <p v-if="isAuthenticated" class="credit-balance t-mono">{{ request?.requiredPoints ? t('paywall.balanceNeeded', { balance: totalPoints, required: request.requiredPoints }) : t('paywall.balance', { balance: totalPoints }) }}</p>
      <div v-if="loading" class="upgrade-loading" role="status" :aria-label="t('common.loading')"><div v-for="index in 2" :key="index" class="plan-skeleton" /></div>
      <div v-else-if="loadError" class="upgrade-state"><p role="alert">{{ t('billing.plansUnavailable') }}</p><MmButton variant="secondary" @click="load">{{ t('billing.checkAgain') }}</MmButton></div>
      <template v-else-if="selectedPlan">
        <MmSegmented class="paywall-period" :model-value="activePeriod" :options="periodOptions" :label="t('billing.billingPeriod')" full @update:model-value="selectPeriod" />
        <fieldset class="upgrade-options" :disabled="!!choosing || checkoutUnavailable">
          <legend class="sr-only">{{ t('billing.choosePlan') }}</legend>
          <label v-for="plan in periodPlans" :key="plan.product_id" class="plan-option" :class="{ selected: selectedPlan.product_id === plan.product_id }">
            <input class="mm-choice-input" type="radio" :name="radioName" :value="plan.product_id" :checked="selectedPlan.product_id === plan.product_id" @change="selectPlan(plan)" />
            <span class="option-copy"><strong class="option-name">{{ planDisplayName(plan, t) }}</strong><span>{{ creditsLabel(plan) }}</span><small v-if="offer(plan).hasIntro">{{ t('billing.renewsMonthly', { price: formatPrice(offer(plan).regular) }) }}</small><small v-else-if="plan.tag === 'year'">{{ t('billing.annualTotal', { price: formatPrice(offer(plan).regular) }) }}</small></span>
            <span class="option-price"><span v-if="offer(plan).hasIntro" class="offer-label">{{ t('billing.firstMonthDiscount', { percent: offer(plan).introPercent }) }}</span><span v-else-if="plan.tag === 'year' && offer(plan).annualPercent > 0" class="offer-label">{{ t('t24d.saving', { percent: offer(plan).annualPercent }) }}</span><s v-if="offer(plan).hasIntro">{{ formatPrice(offer(plan).regular) }}</s><strong>{{ formatPrice(offer(plan).display) }}</strong><small>{{ plan.tag === 'year' ? t('billing.annualMonthlyUnit') : '/ ' + t('billing.unit_' + plan.tag) }}</small></span>
          </label>
        </fieldset>
        <MmButton class="plan-cta" variant="primary" size="lg" :loading="!!choosing" :disabled="checkoutUnavailable" @click="choose(selectedPlan)">{{ hasSubscription ? t('billing.upgradePlan') : t('paywall.continue') }}</MmButton>
      </template>
      <p v-else class="upgrade-state">{{ t(hasSubscription ? 'paywall.memberNoUpgrade' : 'billing.plansUnavailable') }}</p>
      <p v-if="error" class="upgrade-error" role="alert">{{ t(error) }}</p>
      <p class="upgrade-trust">{{ t('t36Billing.catalogNote') }}</p>
      <div class="upgrade-links"><NuxtLink :to="comparePath" @click="onCompare">{{ t('paywall.comparePlans') }}</NuxtLink><MmButton variant="ghost" @click="dismiss">{{ t('mm.billing.maybeLater') }}</MmButton></div>
      <NuxtLink v-if="hasSubscription" class="account-link" to="/billing" @click="closePaywall">{{ t('billing.accountPageTitle') }}</NuxtLink>
    </div>
  </MmDialog>
</template>

<script setup lang="ts">
import { registerAnalyticsProducts } from '~/utils/analytics-core.mjs';
import { checkoutEntry, checkoutParams, checkoutErrorCode } from '~/utils/checkout-analytics.mjs';
import { useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { billingService, type BillingConfig } from '~/services/billingService';
import { musicService } from '~/services/musicService';
import { useAuthState } from '~/composables/useAuthState';
import { usePaywall } from '~/composables/usePaywall';
import '~/components/mm/base/choice.css';
import MmDialog from '~/components/mm/base/MmDialog.vue';
import MmButton from '~/components/mm/base/MmButton.vue';
import MmSegmented from '~/components/mm/base/MmSegmented.vue';
import { planDisplayName, planTier } from '~/components/mm/billing/plan-presentation.mjs';
import { catalogPrice } from '~/utils/catalog-plan-presentation.mjs';
import { trackEvent } from '~/utils/analytics';
import { planOffer, planAmount, isCurrentFastSpringMember } from '~/utils/subscription-offers.mjs';
import { isStripeHostedUrl, canUpgradePlan } from '~/utils/billing-route.mjs';
import { rememberCheckoutSource, pricingPath } from '~/utils/checkout-source.mjs';

interface Plan { product_id: string; group_id: string; tag: string; name: string; price: string; price_desc?: string; points: number }

const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();
const { request, closePaywall } = usePaywall();
const { isAuthenticated, totalPoints, userAccount, user, fetchAndStoreToken, handleSignIn } = useAuthState();

const visible = computed(() => !!request.value);
const analyticsReady = useState('analytics-ready', () => false);
const analyticsChoice = useState('analytics-choice', () => 'unknown');
const viewedRequests = new WeakSet<object>();
let mounted = false;
const plans = ref<Plan[]>([]);
const config = ref<BillingConfig | null>(null);
const loading = ref(false);
const loadError = ref(false);
const choosing = ref('');
const error = ref('');
let loadRequest = 0;
let chooseRequest = 0;
let disposed = false;
const identity = computed(() => `${isAuthenticated.value}:${user.value?.id || ''}`);
const radioName = `paywall-plan-${useId()}`;

const source = computed(() => request.value ? `paywall_${request.value.surface}_${request.value.trigger}` : 'paywall');
const comparePath = computed(() => `${pricingPath(source.value)}&period=${encodeURIComponent(activePeriod.value)}`);
const hasSubscription = computed(() => config.value?.has_subscription === true);
const checkoutUnavailable = computed(() => !hasSubscription.value && config.value?.checkout_available === false);
const introEligible = computed(() => config.value?.intro_eligible === true);
// Membership upgrades remain limited to the exact product IDs the account allows.
const offers = computed(() => {
  if (hasSubscription.value) {
    if (isCurrentFastSpringMember(config.value)) return [];
    return plans.value.filter(plan => canUpgradePlan(config.value, plan.product_id)).sort((a, b) => planAmount(a.price) - planAmount(b.price));
  }
  return plans.value.filter(plan => ['month', 'year'].includes(plan.tag) && plan.group_id !== 'free' && planAmount(plan.price) > 0)
    .sort((a, b) => planAmount(a.price) - planAmount(b.price));
});
const recommendedId = computed(() => offers.value[0]?.product_id || '');
const selectedPeriod = ref('month');
const selectedTier = ref('');
const selectedProductId = ref('');
const availablePeriods = computed(() => ['month', 'year', 'week'].filter(period => offers.value.some(plan => plan.tag === period)));
const activePeriod = computed(() => availablePeriods.value.includes(selectedPeriod.value) ? selectedPeriod.value : availablePeriods.value[0] || 'month');
const periodOptions = computed(() => availablePeriods.value.map(period => ({ value: period, label: t('billing.period_' + period), disabled: !!choosing.value || checkoutUnavailable.value })));
const periodPlans = computed(() => offers.value.filter(plan => plan.tag === activePeriod.value));
const selectedPlan = computed(() => periodPlans.value.find(plan => plan.product_id === selectedProductId.value)
  || periodPlans.value.find(plan => planTier(plan) && planTier(plan) === selectedTier.value) || periodPlans.value[0]);
function selectPeriod(period: string) {
  if (choosing.value || !availablePeriods.value.includes(period)) return;
  if (selectedPlan.value) selectedTier.value = planTier(selectedPlan.value);
  selectedPeriod.value = period;
  selectedProductId.value = '';
}
function selectPlan(plan: Plan) {
  if (choosing.value || checkoutUnavailable.value || !periodPlans.value.includes(plan)) return;
  selectedTier.value = planTier(plan);
  selectedProductId.value = plan.product_id;
}
function resetSelection() { selectedPeriod.value = 'month'; selectedTier.value = ''; selectedProductId.value = ''; }

const title = computed(() => request.value?.trigger === 'download' ? t('paywall.downloadTitle') : t('paywall.creditsTitle'));
const description = computed(() => {
  if (request.value?.trigger === 'download') return t('paywall.downloadDescription');
  return hasSubscription.value ? t('paywall.memberCreditsDescription') : t('paywall.creditsDescription');
});

const offer = (plan: Plan) => planOffer(plan, plans.value, introEligible.value);
const creditsLabel = (plan: Plan) => `${Number(plan.points).toLocaleString(locale.value)} ${t('t24b.credits')}`;
const formatPrice = (value: string | number) => catalogPrice(value, locale.value);
const baseParams = () => ({
  schema_version: 2, entry: checkoutEntry(request.value?.surface, request.value?.trigger), paywall_type: 'subscription',
  trigger: request.value?.trigger || '',
  surface: request.value?.surface || '',
  source: source.value,
  balance: totalPoints.value,
  required_points: request.value?.requiredPoints || 0,
  vip_level: userAccount.value?.vip_level ?? 0,
  page_path: route.path,
});

async function load() {
  const current = ++loadRequest;
  const owner = identity.value;
  loading.value = true; loadError.value = false; error.value = '';
  const [catalog, account] = await Promise.allSettled([
    musicService.getSubscriptionConfig(),
    isAuthenticated.value ? fetchAndStoreToken().then(ok => ok && current === loadRequest && visible.value && owner === identity.value && !disposed ? billingService.config() : null) : Promise.resolve(null),
  ]);
  if (current !== loadRequest || owner !== identity.value || !visible.value || disposed) return;
  if (catalog.status === 'fulfilled' && catalog.value?.code === 200 && Array.isArray(catalog.value.data?.list)) {
    plans.value = catalog.value.data.list;
    registerAnalyticsProducts(plans.value);
  } else {
    loadError.value = true;
  }
  config.value = account.status === 'fulfilled' ? account.value : null;
  loading.value = false;
  if (!loadError.value) trackEvent('paywall_plans_loaded', { ...baseParams(), offers: offers.value.length, has_subscription: hasSubscription.value });
}

async function choose(plan: Plan) {
  if (choosing.value || checkoutUnavailable.value || !visible.value || !offers.value.some(item => item.product_id === plan.product_id)) return;
  const params = { ...baseParams(), product_id: plan.product_id, plan_name: plan.name, ...checkoutParams(plan, config.value, checkoutEntry(request.value?.surface, request.value?.trigger)), price: offer(plan).hasIntro ? offer(plan).display : offer(plan).regular, currency: 'USD', amount_source: 'catalog', recommended: plan.product_id === recommendedId.value };
  trackEvent('paywall_cta_click', params);
  if (!isAuthenticated.value) { await handleSignIn(); return; }
  const current = ++chooseRequest;
  const owner = identity.value;
  const submittedRequest = request.value;
  const isCurrent = () => current === chooseRequest && submittedRequest === request.value && owner === identity.value && visible.value && !disposed;
  choosing.value = plan.product_id; error.value = '';
  try {
    if (!await fetchAndStoreToken()) throw new Error('Authentication unavailable');
    if (!isCurrent()) return;
    trackEvent('checkout_started', params);
    if (hasSubscription.value) {
      const url = await billingService.upgrade(plan.product_id);
      if (!isCurrent()) return;
      trackEvent('purchase_stage', { ...params, provider: 'stripe', stage: 'checkout_ready', result: 'ready' });
      rememberCheckoutSource(params.source, plan.product_id);
      trackEvent('subscription_upgrade_start', { ...params, schema_version: undefined, current_product_id: config.value?.current_product_id });
      window.location.assign(url);
      return;
    }
    const path = await billingService.checkoutDestination(plan.product_id);
    if (!isCurrent()) return;
    if (path === '/billing') { trackEvent('purchase_stage', { ...params, stage: 'account_redirect', result: 'account_redirect' }); closePaywall(); await router.push('/billing'); return; }
    trackEvent('purchase_stage', { ...params, provider: isStripeHostedUrl(path) ? 'stripe' : 'fastspring', stage: 'checkout_ready', result: 'ready' });
    rememberCheckoutSource(params.source, plan.product_id);
    trackEvent('checkout_start', { ...params, schema_version: undefined });
    if (isStripeHostedUrl(path)) { trackEvent('checkout_redirect', { provider: 'stripe', product_id: plan.product_id, source: params.source }); window.location.assign(path); }
    else { closePaywall(); await router.push(path); }
  } catch (failure: any) {
    if (!isCurrent()) return;
    trackEvent('checkout_error', { ...params, error_stage: 'checkout', error_code: checkoutErrorCode(failure) });
    error.value = failure?.response?.status === 409 ? 'billing.upgradeNotAvailable' : 'billing.unavailable';
    trackEvent('paywall_checkout_error', { ...params, status: failure?.response?.status || 0 });
  } finally { if (current === chooseRequest) choosing.value = ''; }
}

function dismiss() {
  if (request.value) trackEvent('paywall_dismiss', baseParams());
  closePaywall();
}
function onCompare() {
  trackEvent('paywall_compare_plans', baseParams());
  closePaywall();
}



function observePaywall() {
  const current = request.value;
  if (!mounted || disposed || !current || viewedRequests.has(current) || !analyticsReady.value || analyticsChoice.value !== 'granted' || document.visibilityState !== 'visible') return;
  if (trackEvent('paywall_view', baseParams())) viewedRequests.add(current);
}
watch([request, analyticsReady, analyticsChoice], observePaywall, { flush: 'post' });
onMounted(() => {
  mounted = true;
  document.addEventListener('visibilitychange', observePaywall);
  observePaywall();
});

watch(request, next => {
  // Every request instance starts a new blocked action, even on the same surface.
  ++loadRequest; ++chooseRequest; choosing.value = '';
  if (!next) return;
  resetSelection();
  void load();
}, { flush: 'sync', immediate: true });
watch(identity, () => {
  ++loadRequest; ++chooseRequest; choosing.value = ''; config.value = null;
  if (visible.value) { resetSelection(); void load(); }
}, { flush: 'sync' });
onBeforeUnmount(() => { disposed = true; ++loadRequest; ++chooseRequest; document.removeEventListener('visibilitychange', observePaywall); });
// The persistent layout owns route cancellation, including before this chunk loads.
</script>

<style scoped>
.upgrade-dialog { display: grid; gap: var(--s-4); }
.credit-balance { color: var(--text-2); font-size: var(--t-small); }
.upgrade-options, .upgrade-loading { display: grid; gap: var(--s-3); min-width: 0; border: 0; padding: 0; margin: 0; }
.paywall-period { margin-bottom: var(--s-1); }
.paywall-period :deep(button) { min-height: 44px; }
.plan-option { display: grid; grid-template-columns: 20px minmax(0, 1fr) minmax(0, 1fr); align-items: center; gap: var(--s-3); width: 100%; padding: var(--s-4); border-radius: var(--r-lg); border: 1px solid var(--line-strong); background: var(--surface); cursor: pointer; }
.plan-option.selected { border-color: var(--accent); background: var(--accent-soft); }
.plan-option:focus-within { outline: 2px solid var(--focus); outline-offset: 3px; }
.upgrade-options:disabled .plan-option { cursor: default; opacity: .6; }
.option-copy { display: grid; gap: var(--s-2); min-width: 0; color: var(--text-2); font-size: var(--t-small); overflow-wrap: anywhere; }
.option-name { color: var(--text); font: 600 var(--t-body)/1.4 var(--font-ui); }
.option-price { display: grid; justify-items: end; gap: var(--s-1); min-width: 0; text-align: end; color: var(--text-2); font: 500 var(--t-micro)/1.5 var(--font-mono); overflow-wrap: anywhere; }
.option-price strong { font-size: var(--t-h2); line-height: 1.1; color: var(--text); }
.option-copy small { font-size: var(--t-micro); line-height: 1.5; }
.offer-label { color: var(--accent-text); font: 600 var(--t-micro)/1.4 var(--font-ui); }
.plan-cta { width: 100%; min-height: 56px; white-space: normal; line-height: 1.4; padding-block: var(--s-3); }
.upgrade-trust { margin: 0; text-align: center; color: var(--text-3); font-size: var(--t-micro); }
.upgrade-links { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: var(--s-3); align-items: center; }
.upgrade-links a, .upgrade-links :deep(.btn) { display: inline-flex; align-items: center; justify-content: center; min-height: 44px; padding: var(--s-2); text-align: center; color: var(--text-2); font: 500 var(--t-small)/1.4 var(--font-ui); white-space: normal; overflow-wrap: anywhere; }
.upgrade-links a:is(:hover, :focus-visible), .account-link:is(:hover, :focus-visible) { text-decoration: underline; text-underline-offset: 4px; }
.account-link { display: inline-flex; align-items: center; justify-content: center; min-height: 44px; color: var(--text-2); font-size: var(--t-small); }
.upgrade-state { color: var(--text-2); line-height: 1.6; }
.upgrade-error { color: var(--danger); background: var(--danger-soft); padding: var(--s-3); border-radius: var(--r-md); }
.plan-skeleton { height: 120px; border-radius: var(--r-lg); background: var(--surface-2); }
@media (max-width: 389px) { .plan-option { padding: var(--s-3); gap: var(--s-2); } .option-price strong { font-size: var(--t-h3); } }
</style>
